import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { IconButton } from '@material-ui/core';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import cx from 'classnames';
import {
  TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL,
  TEACHERS_PERMISSION_DENIED_URL,
  TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
} from 'config/urls';
import { isNil } from 'lodash';
import {
  classroomInsightsStartProcessing,
  useInsightsClassroomTrackerInstanceSectionDetail,
  useLiveClassroomTrackerInstanceSectionStudentsProgress,
  useTrackerInstanceSectionClassroomCommonIssues,
  useTrackerInstanceSectionClassroomInsightsStatus,
  useTrackerInstanceSectionClassroomTomorrowFocus,
  useTrackerInstanceSectionStudentWorksCount
} from 'sdk';

import { LEVEL_LABEL_TO_MASTERY_MAPPER } from 'pages/Teachers/ClassroomInsights/constants';
import {
  GENERAL_CONCEPTUAL_UNDERSTANDING,
  SIGNIFICANT_ERRORS_IN_WORK,
  UNABLE_TO_DIAGNOSE,
  WORK_IS_COMPLETE_AND_CORRECT
} from 'pages/Teachers/Insights/constants';
import { useNavbar } from 'pages/Teachers/shared/TeacherPageLayout';
import { colors } from 'theme/palette';
import { buildFilters } from 'utils/filters';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { notifyErrors } from 'utils/notifications';

import Header from './components/Header';
import LearningGaps from './components/LearningGaps';
import StudentProgress from './components/StudentProgress';
import TomorrowsFocus from './components/TomorrowsFocus';
import Typography from 'components/Typography';
import UserContext from 'components/UserContext';

import styles from './style.module.scss';
import { getRubricValueByRawValue } from './utils';

const ClassroomInsights = () => {
  const { user } = useContext(UserContext);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [isGeneratingAfterError, setIsGeneratingAfterError] = useState(false);
  const [hasClassroomInitialCall, setHasClassroomInitialCall] = useState(false);
  const [lastStatus, setLastStatus] = useState(null);
  const [forceStopLoading, setForceStopLoading] = useState(false);

  const [feedback, setFeedback] = useState(null);

  const [filters, setFilters] = useState({
    studentIds: [],
    masteryLevel: null,
    grade: null,
    submission_status: null,
    sort_by: localStorage.getItem('liveClassroomInsightsSort')
  });

  const history = useHistory();
  const { trackerInstanceSectionId } = useParams();

  const { data: sectionDetails, loading: isLoadingSectionDetails } =
    useInsightsClassroomTrackerInstanceSectionDetail({
      trackerInstanceSectionId
    });

  const { data } = useTrackerInstanceSectionStudentWorksCount({
    trackerInstanceSectionId
  });

  const { navigation } = useNavbar({ title: '' });
  const {
    data: studentsProgressData,
    refetch: refetchStudentsProgress,
    isLoading: isLoadingStudentsProgress
  } = useLiveClassroomTrackerInstanceSectionStudentsProgress({
    trackerInstanceSectionId,
    filters: buildFilters({
      ...filters,
      masteryLevel:
        filters.masteryLevel &&
        LEVEL_LABEL_TO_MASTERY_MAPPER[filters.masteryLevel],
      grade: getRubricValueByRawValue({
        rawValue:
          typeof filters.grade === 'string'
            ? filters.grade?.toLowerCase()
            : filters.grade
      })
    })
  });

  const {
    data: allStudents,
    refetch: refetchAllStudents,
    isLoading: isLoadingAllStudents
  } = useLiveClassroomTrackerInstanceSectionStudentsProgress({
    trackerInstanceSectionId
  });

  const {
    data: tomorrowsFocus,
    refetch: refetchTomorrowFocus,
    isLoading: isLoadingTomorrowFocus
  } = useTrackerInstanceSectionClassroomTomorrowFocus({
    trackerInstanceSectionId
  });

  useEffect(() => {
    navigation.setNoTopNavigation(true);
  }, [navigation]);

  useEffect(() => {
    if (filters && isNil(filters.sort_by)) {
      localStorage.setItem('liveClassroomInsightsSort', 'name');
    }
  }, [filters]);

  useEffect(() => {
    if (isNil(data)) return;
    if (isNil(sectionDetails)) return;
    if (data.student_works_count === 0) {
      trackMixpanelEvent(
        user.id,
        'Live classroom insights empty state encountered.',
        {
          feature: 'live classroom insights',
          view: 'live classroom insights',
          class_id: sectionDetails.section_id,
          normalizedURL:
            process.env.REACT_APP_BASE_FRONTEND_URL +
            TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
        }
      );
      history.push(TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL);
    }
  }, [data, history, user.id, sectionDetails]);

  useEffect(() => {
    if (isNil(sectionDetails)) return;
    if (sectionDetails.deleted) {
      history.push(TEACHERS_PERMISSION_DENIED_URL);
    }
  }, [sectionDetails, history]);

  const { data: insightsStatus, refetch: refetchInsightsStatus } =
    useTrackerInstanceSectionClassroomInsightsStatus({
      trackerInstanceSectionId
    });

  const {
    data: commonIssues,
    refetch: refetchCommonIssues,
    isLoading: isLoadingCommonIssues
  } = useTrackerInstanceSectionClassroomCommonIssues({
    trackerInstanceSectionId
  });

  useEffect(() => {
    if (typeof refetchInsightsStatus !== 'function') return;

    const interval = setInterval(() => {
      refetchInsightsStatus();
      setLastStatus(insightsStatus);
    }, 15000);

    return () => clearInterval(interval);
  }, [refetchInsightsStatus, insightsStatus]);

  useEffect(() => {
    const interval = setInterval(() => {
      refetchStudentsProgress();
      refetchAllStudents();
    }, 15000);
    return () => clearInterval(interval);
  }, [refetchStudentsProgress, refetchAllStudents]);

  useEffect(() => {
    if (insightsStatus === 'processing' || isLoadingInsights) {
      const interval = setInterval(() => {
        refetchTomorrowFocus();
        refetchCommonIssues();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [
    insightsStatus,
    isLoadingInsights,
    refetchTomorrowFocus,
    refetchCommonIssues
  ]);

  useEffect(() => {
    if (insightsStatus !== 'error') {
      setIsGeneratingAfterError(false);
    }

    if (insightsStatus === 'up_to_date' && isLoadingInsights === true) {
      setIsLoadingInsights(false);

      refetchCommonIssues();
      refetchTomorrowFocus();
    }

    if (forceStopLoading && isLoadingInsights) {
      setIsLoadingInsights(false);
      setForceStopLoading(false);

      refetchCommonIssues();
      refetchTomorrowFocus();
    }

    if (
      insightsStatus === 'error' &&
      isLoadingInsights === true &&
      !isGeneratingAfterError
    ) {
      setIsLoadingInsights(false);
    }
  }, [
    insightsStatus,
    isLoadingInsights,
    refetchCommonIssues,
    refetchTomorrowFocus,
    isGeneratingAfterError,
    forceStopLoading
  ]);

  const aboveMasteryStudentCount = useMemo(() => {
    if (!studentsProgressData) return 0;

    return studentsProgressData.filter(
      (student) => student.tag_description === WORK_IS_COMPLETE_AND_CORRECT
    ).length;
  }, [studentsProgressData]);

  const aboveMasteryStudents = useMemo(() => {
    if (isNil(allStudents)) return null;

    return allStudents.filter(
      (student) => student.tag_description === WORK_IS_COMPLETE_AND_CORRECT
    );
  }, [allStudents]);

  const atMasteryStudentCount = useMemo(() => {
    if (!studentsProgressData) return 0;

    return studentsProgressData.filter(
      (student) => student.tag_description === GENERAL_CONCEPTUAL_UNDERSTANDING
    ).length;
  }, [studentsProgressData]);

  const belowMasteryStudentCount = useMemo(() => {
    if (!studentsProgressData) return 0;

    return studentsProgressData.filter(
      (student) => student.tag_description === SIGNIFICANT_ERRORS_IN_WORK
    ).length;
  }, [studentsProgressData]);

  const needsReviewStudentCount = useMemo(() => {
    if (!studentsProgressData) return 0;
    return studentsProgressData.filter(
      (student) =>
        student.tag_description === UNABLE_TO_DIAGNOSE ||
        student.tag_description === 'Needs Review'
    ).length;
  }, [studentsProgressData]);

  const handleGenerateInsights = useCallback(async () => {
    setIsLoadingInsights(true);

    if (insightsStatus === 'error') {
      setIsGeneratingAfterError(true);
    }

    const { success, errors } = await classroomInsightsStartProcessing({
      trackerInstanceSectionId
    });

    if (!success) {
      notifyErrors(errors);
      setIsLoadingInsights(false);
    } else {
      refetchCommonIssues();
      refetchTomorrowFocus();
    }

    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'live classroom insights',
        view: 'classroom insights header',
        button: 'regenerate insights',
        class_id: data?.section_id,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      }
    );
  }, [
    insightsStatus,
    trackerInstanceSectionId,
    user.id,
    data?.section_id,
    refetchCommonIssues,
    refetchTomorrowFocus
  ]);

  useEffect(() => {
    if (hasClassroomInitialCall) return;
    if (!insightsStatus) return;
    if (
      aboveMasteryStudentCount +
        belowMasteryStudentCount +
        atMasteryStudentCount ===
      0
    )
      return;

    if (insightsStatus === 'up_to_date') {
      setHasClassroomInitialCall(true);
      return;
    }

    handleGenerateInsights();
    setHasClassroomInitialCall(true);
  }, [
    insightsStatus,
    hasClassroomInitialCall,
    aboveMasteryStudentCount,
    belowMasteryStudentCount,
    atMasteryStudentCount,
    handleGenerateInsights
  ]);

  const loadingCount = useMemo(() => {
    if (!studentsProgressData) return 0;
    return studentsProgressData.filter(
      (student) => student.tag_description === 'Loading'
    ).length;
  }, [studentsProgressData]);

  useEffect(() => {
    if (
      insightsStatus === 'out_of_date' &&
      ['processing', 'no_data'].includes(lastStatus)
    ) {
      setForceStopLoading(true);
    }
    if (insightsStatus === 'not_allowed' && loadingCount === 0) {
      setForceStopLoading(true);
    }
  }, [insightsStatus, lastStatus, loadingCount]);

  useEffect(() => {
    if (
      ['out_of_date', 'error'].includes(insightsStatus) &&
      !isLoadingInsights &&
      !isGeneratingAfterError
    ) {
      handleGenerateInsights();
    }
  }, [
    insightsStatus,
    isLoadingInsights,
    isGeneratingAfterError,
    handleGenerateInsights
  ]);

  useEffect(() => {
    if (
      ['out_of_date', 'error'].includes(lastStatus) &&
      ['out_of_date', 'error'].includes(insightsStatus) &&
      !isLoadingInsights &&
      !isGeneratingAfterError
    ) {
      handleGenerateInsights();
    }
  }, [
    lastStatus,
    insightsStatus,
    isLoadingInsights,
    isGeneratingAfterError,
    handleGenerateInsights
  ]);

  return (
    <div className={styles.pageContainer}>
      <Header
        data={sectionDetails}
        dataIsLoading={isLoadingSectionDetails}
        aboveCount={aboveMasteryStudentCount}
        atCount={atMasteryStudentCount}
        belowCount={belowMasteryStudentCount}
        needsReviewCount={needsReviewStudentCount}
        loadingCount={loadingCount}
        onPlusButtonClick={() =>
          history.push(TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL)
        }
      />
      <div className={styles.container}>
        <div className={styles.main}>
          <div className={styles.layout}>
            <div className={styles.leftContainer}>
              <section className={styles.card}>
                <StudentProgress
                  loadingCount={loadingCount}
                  students={studentsProgressData}
                  allStudents={allStudents}
                  isLoadingData={isLoadingStudentsProgress}
                  objectiveValueType={sectionDetails?.value_type}
                  onActionSuccess={() => {
                    refetchStudentsProgress();
                    refetchAllStudents();
                    refetchInsightsStatus();
                  }}
                  sectionId={sectionDetails?.section_id}
                  filters={filters}
                  setFilters={setFilters}
                />
              </section>

              <section className={styles.card}>
                <LearningGaps
                  commonIssues={
                    commonIssues ? commonIssues['common_issues'] : null
                  }
                  isLoading={
                    isLoadingCommonIssues ||
                    ['not_allowed'].includes(insightsStatus)
                  }
                  isProcessing={
                    insightsStatus === 'processing' || isLoadingInsights
                  }
                  lastUpdatedAt={commonIssues?.created_at}
                />
              </section>
            </div>
            <section className={styles.card}>
              <TomorrowsFocus
                celebrationsStudents={aboveMasteryStudents}
                tomorrowsFocus={
                  tomorrowsFocus ? tomorrowsFocus['suggested_moves'] : []
                }
                isLoading={
                  isLoadingTomorrowFocus ||
                  ['not_allowed'].includes(insightsStatus) ||
                  isLoadingAllStudents
                }
                isProcessing={
                  insightsStatus === 'processing' || isLoadingInsights
                }
                lastUpdatedAt={tomorrowsFocus?.created_at}
              />
            </section>
          </div>
        </div>
      </div>
      <div className={styles.feedbackContainer}>
        <Typography variant="S-TEXT-2" color={colors.pink}>
          Was this overview helpful?
        </Typography>
        <IconButton
          onClick={() => {
            trackMixpanelEvent(user.id, 'Live classroom insights feedback', {
              feature: 'live classroom insights',
              view: 'rate feedback',
              feedback: 'helpful',
              class_id: sectionDetails.section_id,
              normalizedURL:
                process.env.REACT_APP_BASE_FRONTEND_URL +
                TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
            });
            setFeedback('helpful');
          }}
          className={cx({ [styles.activeFeedback]: feedback === 'helpful' })}
        >
          <ThumbUpIcon />
        </IconButton>
        <IconButton
          onClick={() => {
            trackMixpanelEvent(user.id, 'Live classroom insights feedback', {
              feature: 'live classroom insights',
              view: 'rate feedback',
              feedback: 'not helpful',
              class_id: sectionDetails.section_id,
              normalizedURL:
                process.env.REACT_APP_BASE_FRONTEND_URL +
                TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
            });
            setFeedback('not_helpful');
          }}
          className={cx({
            [styles.activeFeedback]: feedback === 'not_helpful'
          })}
        >
          <ThumbDownIcon />
        </IconButton>
      </div>
    </div>
  );
};

export default ClassroomInsights;
